import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export const DeleteProjectDialog = ({ project, open, onOpenChange, onDeleted }) => {
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && project) { setImpact(null); api.get(`/projects/${project.id}/delete-impact`).then((r) => setImpact(r.data)).catch((e) => toast.error(errMsg(e))); } }, [open, project]);
  const del = async () => {
    setBusy(true);
    try { await api.delete(`/projects/${project.id}`); toast.success(`${project.project_code} moved to Deleted Projects`); onOpenChange(false); onDeleted?.(project); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!project) return null;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="delete-project-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this project?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              <div><span className="font-mono font-medium text-slate-800">{project.project_code}</span> · <span className="font-medium text-slate-800" data-testid="delete-project-name">{project.name}</span></div>
              <div>It will be removed from active project views. You can restore it from Deleted Projects.</div>
              {impact && (impact.invoices > 0 || impact.receipts > 0) && (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900" data-testid="delete-financial-impact">
                  This project has <b>{impact.invoices}</b> invoice(s) and <b>{impact.receipts}</b> receipt(s). They stay in Payments labelled “Deleted project”; nothing is cancelled and outstanding receivables are unchanged.
                </div>
              )}
              {impact && <div className="text-xs text-slate-500">{impact.documents} file(s), the checklist, source references and activity are kept for restoration. Client, contacts, team members and company agreements are not affected.</div>}
              {impact && !impact.can_delete && <div className="text-xs text-rose-700" data-testid="delete-no-permission">Only administrators or this project's manager can delete it.</div>}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-project-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" disabled={busy || !impact?.can_delete} onClick={(e) => { e.preventDefault(); del(); }} data-testid="delete-project-confirm"><Trash2 className="h-4 w-4 mr-1" />{busy ? "Deleting…" : "Delete project"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
